import React, {
    useEffect,
    useState,
    useCallback,
    useRef,
    useLayoutEffect,
    useMemo,
} from 'react';
import {genericMessageHandler, formatPrice, Icon} from 'shared/helpers';
import {isMobile} from 'react-device-detect';
import {useAppContext, useJobContext, useNotificationContext} from 'contexts';
import {ProductDetail, ProductQuantity} from 'components';
import {useNavigate} from 'react-router-dom';
import {cloneDeep, debounce, isEqual, isEmpty} from 'lodash';
import {
    invalidateProductAndCost,
    useDeleteCabinetMutation,
    useGetRoomProductsQuery,
    useUpdateOrderMutation,
} from 'components/customer/Product/store/productApi';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {
    productsLoadedSet,
    productsLoadedState,
} from 'components/customer/Product/store/roomProductsSlice';
import {Dimensions} from 'components/customer/JobDashboard/columns/Dimensions';
import {Total} from 'components/customer/JobDashboard/columns/Total';

const getNoRecordMessage = (
    userProfile,
    job,
    showDialog,
    addProductUrl,
    doorsAndPanelsUrl,
    navigate
) => {
    if (job && job.variationsConfirmed == 1) {
        return <>No Products</>;
    }

    const fullTextDisplay = [];
    fullTextDisplay.push(
        userProfile.isAddProductAvailable ? (
            <span
                key={0}
                onClick={() => navigate(addProductUrl)}
                style={{cursor: 'pointer'}}>
                Add Product{' '}
            </span>
        ) : null
    );
    fullTextDisplay.push(
        userProfile.isQFPAvailable ? (
            <span
                key={1}
                onClick={() =>
                    isMobile
                        ? showDialog({
                              title: userProfile.qFPMenuText,
                              message:
                                  'This feature is only available in desktop version.',
                              hideYesButton: true,
                              hideNoButton: true,
                              keyboard: false,
                              buttons: [
                                  {
                                      name: 'Ok',
                                      show: true,
                                  },
                              ],
                          })
                        : navigate(doorsAndPanelsUrl)
                }
                style={{cursor: 'pointer'}}>
                , {userProfile.qFPMenuText}{' '}
            </span>
        ) : null
    );

    return (
        <>
            No Products.{' '}
            <span style={{color: 'red'}}>
                <strong>Click {fullTextDisplay.filter(Boolean)}</strong> to add
                one.
            </span>
        </>
    );
};

const getTableConfig = (
    dragDropType,
    userProfile,
    actionButtonsEnabled,
    job,
    room,
    isJobProcessing,
    showDialog,
    navigate,
    roomIndex
) => {
    const addProductUrl = `/v2/job/${job.displayId}/room/${room.id}/product`;
    const doorsAndPanelsUrl = `/v2/job/${job.displayId}/room/${room.id}/quick-flat-product`;

    const tableConfig = {
        fields: [
            {
                fieldName: 'room_cab_number',
                title: '#',
                width: 35,
                minWidth: 38,
                rowAction: false,
                format: (value) => {
                    if (!isJobProcessing) {
                        return (
                            <strong>
                                {roomIndex + 1}-{value}
                            </strong>
                        );
                    } else {
                        return (
                            <>
                                {roomIndex + 1}-{value}
                            </>
                        );
                    }
                },
            },
            {
                fieldName: 'typeName',
                title: <div>Product</div>,
                format: (field, row) => {
                    return <ProductDetail product={row} />;
                },
            },
            {
                fieldName: 'dimensionString',
                mobile: false,
                title: <div>Dimensions (mm)</div>,
                width: 170,
                rowAction: false,
                format: function DimensionField(field, row) {
                    return <Dimensions productDetails={row} />;
                },
            },
            userProfile.show_pricing && {
                fieldName: 'cost',
                mobile: false,
                title: (
                    <div>
                        Unit Price (ex.{' '}
                        {job.countryTaxRateName
                            ? job.countryTaxRateName
                            : 'Tax'}
                        )
                    </div>
                ),
                width: 150,
                minWidth: 90,
                format: function FormatUnitPrice(value, row) {
                    return formatPrice(value / Number(row.quantity), row);
                },
            },
            {
                fieldName: 'quantity',
                mobile: false,
                title: <div>Quantity</div>,
                width: 190,
                minWidth: 85,
                rowAction: false,
                format: function PriceField(field, row) {
                    if (!actionButtonsEnabled || !isJobProcessing) {
                        return field;
                    }
                    return (
                        <ProductQuantity value={field} productDetails={row} />
                    );
                },
            },
            userProfile.show_pricing && {
                fieldName: 'cost',
                mobile: false,
                title: (
                    <div>
                        TOTAL (ex.{' '}
                        {job.countryTaxRateName
                            ? job.countryTaxRateName
                            : 'Tax'}
                        )
                    </div>
                ),
                width: 150,
                minWidth: 70,
                format: function FormatTotal(value, row) {
                    return <Total value={formatPrice(value, row)} />;
                },
            },
        ].filter((column) => column),
        hasCursor: true,
        noRecordsText: getNoRecordMessage(
            userProfile,
            job,
            showDialog,
            addProductUrl,
            doorsAndPanelsUrl,
            navigate
        ),
        cellSpacing: 0,
    };

    if (isJobProcessing) {
        tableConfig.reorder = {
            allowReorder: true,
            dragHandle: 0,
            type: dragDropType,
        };

        tableConfig.options = {
            title: <div>Actions</div>,
            width: isMobile ? 50 : 150,
            buttons: [],
        };
    }

    return tableConfig;
};

const getButtonsConfig = (editCabinet, removeProduct) => {
    return [
        {
            iconName: 'Options-Edit.svg',
            title: 'Edit Product',
            rowAction: true,
            action: (row, event) =>
                editCabinet(row, false, row.in_qfp_summary == 1, event),
        },
        {
            iconName: 'Options-Copy.svg',
            title: 'Copy Product',
            action: (row, event) => editCabinet(row, true, false, event),
            isHidden: (row) => row.in_qfp_summary == 1,
        },
        {
            iconName: 'Options-Delete.svg',
            title: 'Delete Product',
            action: removeProduct,
        },
    ];
};

const cabinetMapper =
    (roomIndex, limit = 0) =>
    (cabinet, index) => {
        return {
            ...cabinet,
            ...{
                roomCabNumber: index + limit + 1,
            },
        };
    };

const orderFieldFormatter = (
    tableConfig,
    reorderItems,
    type,
    totalItems,
    roomCabNumber
) => {
    tableConfig.fields = tableConfig.fields.map((field) => {
        if (field.fieldName == 'room_cab_number') {
            field.format = (
                value,
                row,
                fieldName,
                index,
                showDragHandle,
                dragReference
            ) => {
                return (
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        {showDragHandle ? (
                            <div
                                ref={dragReference}
                                title="Drag product to reorder"
                                style={{marginBottom: '5px', cursor: 'grab'}}>
                                <Icon
                                    iconName="Drag.svg"
                                    style={{width: '30px'}}
                                />
                            </div>
                        ) : null}
                        <div>
                            <Icon
                                iconName="Button-Up-Table.svg"
                                style={{
                                    cursor: 'pointer',
                                    opacity: index == 0 ? 0.5 : 1,
                                    width: '28px',
                                }}
                                disabled={index == 0}
                                onClick={reorderItems(index, index - 1, type)}
                            />
                            <strong>
                                {roomCabNumber + 1}-{value}
                            </strong>
                            <Icon
                                iconName="Button-Down-Table.svg"
                                style={{
                                    cursor: 'pointer',
                                    opacity: index == totalItems - 1 ? 0.5 : 1,
                                    width: '28px',
                                }}
                                disabled={index == totalItems - 1}
                                onClick={reorderItems(index, index + 1, type)}
                            />
                        </div>
                    </div>
                );
            };
        }

        return field;
    });

    return tableConfig;
};

export const useJobDashboard = () => {
    const dispatch = useAppDispatch();
    const {
        actionButtonsEnabled,
        job,
        room,
        showDialog,
        isJobProcessing,
        roomIndex,
    } = useJobContext();

    const {notify} = useNotificationContext();
    const {userProfile, setIsMobileMenuVisible} = useAppContext();
    const navigate = useNavigate();

    const [qfps] = useState([]);
    const [products, setProducts] = useState([]);
    const [productTableConfig, setProductTableConfig] = useState();
    const [productLoaded, setProductLoaded] = useState(false);
    const productsLoaded = useAppSelector(productsLoadedState);

    const [deleteProduct] = useDeleteCabinetMutation();
    const [updateOrder] = useUpdateOrderMutation();

    const {
        data: {products: productData} = {},
        isLoading,
        isFetching,
        isSuccess,
    } = useGetRoomProductsQuery(
        {roomId: parseInt(room.id)},
        {
            skip: typeof room.id === 'undefined',
        }
    );

    const reorder = useRef(
        debounce(async (updatedProducts, jobId, roomId) => {
            const newOrder = {};
            updatedProducts.forEach((product, index) => {
                newOrder[product.id] = index + 1;
            });

            await updateOrder({jobId, roomId, order: newOrder}).unwrap();
            setProducts(updatedProducts);
        }, 2000)
    ).current;

    const removeProduct = (product, event) => {
        event.stopPropagation();
        const button = event.target.parentElement;
        showDialog({
            title: 'Delete item',
            message: 'Are you sure you want to delete this item from the job?',
            yes: async () => {
                try {
                    button.disabled = true;
                    await deleteProduct({cabinetId: product.id});
                    dispatch(invalidateProductAndCost());
                    dispatch(invalidateTotalProductCount());

                    button.disabled = false;
                    genericMessageHandler(
                        notify,
                        {message: 'Item removed.'},
                        'success'
                    );
                } catch (e) {
                    button.disabled = false;
                    genericMessageHandler(notify, e);
                }
            },
        });
    };

    const editCabinet = (row, copy = false, isQFP = false, event) => {
        event.stopPropagation();
        let url = `/v2/job/${job.displayId}/room/${room.id}/product?product=${row.type}&cabinetId=${row.id}`;

        if (copy) {
            url += '&copy';
        }

        if (isQFP) {
            url = `/v2/job/${job.displayId}/room/${room.id}/quick-flat-product?edit=${row.id}`;
        }

        navigate(url);
    };

    const hasRoom = useMemo(() => {
        return job?.rooms?.length >= 1;
    }, [job]);

    const reorderItems = useCallback(
        (products) => {
            if (job && job.id && room && room.id) {
                setProducts(products);
                reorder(products, job.id, room.id);
            }
        },
        [job, room]
    );

    const reorderItemsManual = useCallback(
        (orderIndex, hoverIndex, type) => () => {
            if (job && job.id && room && room.id) {
                let updatedProducts = cloneDeep(products);

                updatedProducts.splice(
                    hoverIndex,
                    0,
                    updatedProducts.splice(orderIndex, 1)[0]
                );

                updatedProducts = updatedProducts.map(
                    cabinetMapper(roomIndex, qfps.length)
                );

                setProducts(updatedProducts);
                reorder(updatedProducts, job.id, room.id);
            }
        },
        [qfps, products, job, room, roomIndex]
    );

    useEffect(() => {
        // productData is sometimes served with empty cached value []
        // we need this cleanup function to ensure we only removed the loader after data is actually fetched
        return () => {
            dispatch(productsLoadedSet(false));
        };
    }, []);

    useEffect(() => {
        if (productData) {
            setProducts((prevState) =>
                isEqual(prevState, productData) ? prevState : productData
            );
            if (productsLoaded) {
                setProductLoaded(true);
            }
        }
    }, [productData, productsLoaded]);

    useEffect(() => {
        if (productsLoaded && isEmpty(room)) {
            // clear products associated when room is removed
            setProducts([]);
            setProductLoaded(true);
        }
    }, [room, productsLoaded]);

    useEffect(() => {
        if (isLoading || isFetching) {
            setProductLoaded(false);
        }
    }, [isLoading, isFetching]);

    useEffect(() => {
        if (
            !isLoading &&
            !isFetching &&
            !productsLoaded &&
            isSuccess &&
            products?.length === 0
        ) {
            // handling when empty cache data is returned
            setProductLoaded(true);
        }
    }, [isLoading, isFetching, isSuccess, productsLoaded, products]);

    useEffect(() => {
        const addProductUrl = `/v2/job/${job.displayId}/room/${room.id}/product`;
        const doorsAndPanelsUrl = `/v2/job/${job.displayId}/room/${room.id}/quick-flat-product`;
        const addRoomUrl = `/v2/job/${job.displayId}/room`;

        if (Object.keys(userProfile).length > 0) {
            let tableConfig;
            if (typeof productTableConfig === 'undefined' || !isJobProcessing) {
                tableConfig = getTableConfig(
                    'Product-table-row',
                    userProfile,
                    actionButtonsEnabled,
                    job,
                    room,
                    isJobProcessing,
                    showDialog,
                    navigate,
                    roomIndex
                );
            } else {
                tableConfig = cloneDeep(productTableConfig);

                if (
                    isJobProcessing &&
                    typeof tableConfig.reorder === 'undefined'
                ) {
                    tableConfig.reorder = {
                        allowReorder: true,
                        dragHandle: 0,
                        type: 'Product-table-row',
                    };
                }
            }

            if ([0, 11, 19].includes(job?.status) && job?.accepted == 0) {
                if (!tableConfig.options) {
                    tableConfig.options = {
                        title: <div>Actions</div>,
                        width: isMobile ? 50 : 150,
                        buttons: getButtonsConfig(editCabinet, removeProduct),
                        isHidden: false,
                    };
                }
            }

            if (isJobProcessing) {
                if (tableConfig.options) {
                    tableConfig.options.isHidden = !actionButtonsEnabled;
                    tableConfig.options.buttons = getButtonsConfig(
                        editCabinet,
                        removeProduct
                    );
                }
                if (actionButtonsEnabled) {
                    if (tableConfig.reorder !== undefined) {
                        tableConfig.reorder.order = reorderItems;
                    }
                    tableConfig = orderFieldFormatter(
                        tableConfig,
                        reorderItemsManual,
                        'Product-table-row',
                        products.length,
                        roomIndex
                    );
                }
            }

            if (products.length) {
                tableConfig.data = products;
            } else {
                tableConfig.data = productLoaded ? [] : undefined;
            }

            if (Object.keys(job).length == 0) {
                tableConfig.noRecordsText = '';
            } else if (!hasRoom) {
                tableConfig.data = [];
                tableConfig.noRecordsText = (
                    <>
                        No Room has been added to the current job. Click{' '}
                        <span
                            style={{color: 'red', cursor: 'pointer'}}
                            onClick={() => navigate(addRoomUrl)}>
                            <strong>Add Room</strong> to set preferred cabinet
                            defaults.
                        </span>
                    </>
                );
            } else {
                tableConfig.noRecordsText = getNoRecordMessage(
                    userProfile,
                    job,
                    showDialog,
                    addProductUrl,
                    doorsAndPanelsUrl,
                    navigate
                );
            }

            if (tableConfig && tableConfig.options) {
                tableConfig.options.alignment = 'center';
            }

            setProductTableConfig((prevState) =>
                isEqual(prevState, tableConfig) ? prevState : tableConfig
            );
        }
    }, [
        hasRoom,
        products,
        userProfile,
        actionButtonsEnabled,
        isJobProcessing,
        job,
        room,
        roomIndex,
        productLoaded,
    ]);

    useLayoutEffect(() => {
        setIsMobileMenuVisible(true);
    }, []);

    return {
        productTableConfig,
        products,
    };
};
